import React, { useState } from 'react';
import { Video, MessageCircle, Calendar, Clock, Check } from 'lucide-react';
import PageHero, { Section, InfoCard, CTABlock } from '../components/PageHero';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';

const slots = ['10:00 AM', '11:30 AM', '1:00 PM', '3:00 PM', '5:00 PM', '7:00 PM'];
const topics = ['University Selection', 'Fee Structure & Budgeting', 'Admission Process', 'FMGE / NExT Guidance', 'Life in Russia', 'Visa & Travel', 'Post-arrival Support'];

export default function VideoCounselling() {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', date: '', slot: '', topic: '', neet: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.slot) { toast({ title: 'Please fill Name, Phone & Slot', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await submitInquiry({
        type: 'video',
        name: f.name,
        phone: f.phone,
        email: f.email || undefined,
        date: f.date || undefined,
        slot: f.slot,
        topic: f.topic || undefined,
        neet: f.neet || undefined,
      });
      toast({ title: 'Session Booked!', description: `Zoom link will be shared 30 minutes before your ${f.slot} session.` });
      setF({ name: '', phone: '', email: '', date: '', slot: '', topic: '', neet: '' });
    } catch (err) {
      toast({ title: 'Could not book session', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="1-on-1 Video Session" title="Free Video" highlight="Counselling"
        subtitle="Book a face-to-face online session with a verified salaried counsellor. Pick your topic, choose your slot, and get personalised guidance from home."
        breadcrumbs={[{ label: 'Video Counselling' }]} />

      <Section>
        <div className="grid lg:grid-cols-[1fr_440px] gap-10 items-start">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Why Video Counselling?</h3>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <InfoCard icon={Video} title="Face-to-Face on Zoom">Screen-share fees, universities, and admission timeline live during the call.</InfoCard>
              <InfoCard icon={MessageCircle} title="Involve Parents">Multi-participant call — parents can join from a different city/device.</InfoCard>
              <InfoCard icon={Clock} title="45–60 Minute Slots">Enough time to answer every question, review your NEET score & shortlist universities.</InfoCard>
              <InfoCard icon={Check} title="100% Free">No hidden fees. Salaried counsellors — zero commission bias.</InfoCard>
            </div>

            <div className="mt-8">
              <h4 className="font-serif text-xl font-bold text-primary">Popular Discussion Topics</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {topics.map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-brand/10 text-brand text-xs font-bold">{t}</span>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-border shadow-lg p-6">
            <div className="text-xs uppercase tracking-widest text-brand font-bold">Book Your Slot</div>
            <h4 className="font-serif text-xl font-bold text-primary mt-1">Free 1-on-1 Session</h4>
            <div className="mt-4 space-y-3">
              <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Full Name *" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <input value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} type="tel" placeholder="WhatsApp Number *" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <input value={f.email} onChange={e=>setF({...f,email:e.target.value})} type="email" placeholder="Email" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <input value={f.date} onChange={e=>setF({...f,date:e.target.value})} type="date" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <select value={f.slot} onChange={e=>setF({...f,slot:e.target.value})} className="w-full h-10 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                <option value="">Preferred Time *</option>
                {slots.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={f.topic} onChange={e=>setF({...f,topic:e.target.value})} className="w-full h-10 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                <option value="">Discussion Topic</option>
                {topics.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={f.neet} onChange={e=>setF({...f,neet:e.target.value})} placeholder="NEET Score (optional)" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <Button type="submit" className="w-full bg-brand hover:bg-brand-dark text-white h-11 rounded-lg font-semibold btn-shift">Book Session</Button>
            </div>
            <p className="mt-3 text-[10px] text-muted-foreground text-center">Zoom link will be shared 30 mins before session on WhatsApp.</p>
          </form>
        </div>
      </Section>

      <CTABlock title="Prefer WhatsApp instead?" subtitle="Quick text-based counselling on WhatsApp — responses within 15 minutes." cta="Chat on WhatsApp" />
    </>
  );
}
